// legal.js - The published legal documents. They live as markdown in
// src/content/legal for the same reasons the articles do: version controlled,
// rendered at build time, no CMS, and a diff for every change, which is what
// proves what a document said on the day somebody agreed to it.
//
// Write each paragraph on one line. These render with `breaks: true`, so a
// newline inside a paragraph becomes a line break on the page.
//
// IMPORTANT. The `version` and `effective` in each file's front matter must
// match server/src/lib/legal.js, and both must be changed in the same commit as
// the text they describe. The server is what stamps the version onto a stored
// agreement; these values are what a reader sees. If they drift, the record and
// the page disagree, which is the one failure this whole design exists to stop.
//
// The consent sentences below are also duplicated on the server. The server's
// copy is the one written into the record, so keep the two identical.
//
// LEGEND
//   1. LEGAL_DOCS      every document, in reading order
//   2. getLegalDoc()   one document with its rendered HTML and headings
//   3. CONSENT         the wording beside each tick box
//   4. requiredAt()    which documents a moment asks you to accept

import { marked } from "marked";
import { parseFrontMatter } from "./content.js";

////////// 1. THE DOCUMENTS //////////

const files = import.meta.glob("../content/legal/*.md", { eager: true, query: "?raw", import: "default" });

export const LEGAL_DOCS = Object.entries(files)
  .map(([path, raw]) => {
    const slug = path.split("/").pop().replace(/\.md$/, "");
    const { meta, body } = parseFrontMatter(raw);
    return {
      key: meta.key || slug,
      slug,
      title: meta.title || slug,
      summary: meta.summary || "",
      version: meta.version || "1.0",
      effective: meta.effective || "",
      updated: meta.updated || meta.effective || "",
      order: Number(meta.order) || 99,
      // Reachable at its own address, never in a list. The affiliate terms are
      // for affiliates, and a customer browsing /legal has no reason to meet
      // them. See server/src/lib/legal.js.
      unlisted: meta.unlisted === "true" || meta.unlisted === true,
      body,
    };
  })
  .sort((a, b) => a.order - b.order);

export const LEGAL_KEYS = LEGAL_DOCS.map((doc) => doc.key);

////////// 2. ONE DOCUMENT //////////
// Headings come back with the document because the contents list and the anchor
// on each heading have to agree, and deriving both from one pass is the only way
// they cannot fall out of step.

// token.text is raw markdown, so emphasis and link syntax come through with it.
// The contents list wants the words, not the punctuation that styles them.
const plain = (text) =>
  text.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1").replace(/[*_`]/g, "").trim();

const slugify = (text) =>
  plain(text).toLowerCase().replace(/[^\w\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 60);

export function getLegalDoc(key) {
  const doc = LEGAL_DOCS.find((entry) => entry.key === key);
  if (!doc) return null;

  const headings = [];
  const renderer = new marked.Renderer();
  const base = renderer.heading.bind(renderer);
  renderer.heading = function heading(token) {
    const text = this.parser.parseInline(token.tokens);
    if (token.depth === 2) {
      const id = slugify(token.text);
      headings.push({ id, text: plain(token.text) });
      return `<h2 id="${id}">${text}</h2>\n`;
    }
    return base(token);
  };

  // breaks: true, unlike the articles. A legal document is full of postal
  // addresses and contact blocks, and every one of them has to keep its own
  // lines. Every paragraph in src/content/legal is written on one line for
  // exactly this reason, so a soft wrap there is always a deliberate break.
  const html = marked.parse(doc.body, { renderer, breaks: true });
  return { ...doc, html, headings };
}

////////// 3. CONSENT WORDING //////////
// Kept identical to CONSENT_PROMPTS in server/src/lib/legal.js.

export const CONSENT = {
  request:
    "I have read and agree to the Terms and Conditions, the Privacy Policy, and the "
    + "Shipping, Insurance and Returns Policy. I confirm I am at least 18 and that "
    + "anything I send is mine to sell.",
  purchase:
    "I have read and agree to the Customer Purchase Agreement and Bill of Sale. I "
    + "understand that accepting payment transfers ownership of these items to "
    + "International Gold and Silver and that the sale is final.",
  affiliate:
    "I have read and agree to the Affiliate Terms and the Privacy Policy. I am "
    + "applying on behalf of myself or a business I am authorised to bind, and I "
    + "understand that commission rates are set by International Gold and Silver "
    + "and may change, that nothing is owed until a customer I refer is paid, and "
    + "that an application is not an agreement to work with me until it is "
    + "accepted.",
  sms:
    "Text me when my package arrives and when my offer is ready. Message and data "
    + "rates may apply. Reply STOP at any time.",
  // Word for word CONSENT_PROMPTS.alertSms on the server, which is the string
  // stamped onto the stored consent record. A price alert is agreed to on its
  // own form by somebody who may never send us anything, so it cannot borrow
  // the wording above.
  alertSms:
    "Text me price alerts. I agree to receive precious metal price alerts by text "
    + "from International Gold and Silver at the number I gave. Message frequency "
    + "varies with the market. Message and data rates may apply. Reply STOP to "
    + "cancel or HELP for help. Carriers are not liable for delayed or undelivered "
    + "messages. See the Text Message Terms and Privacy Policy. This is a free "
    + "service and consent is not a condition of any purchase or sale.",
};

////////// 4. WHAT A MOMENT ASKS FOR //////////
// Mirrors `moments` in the server register. Used to print the links next to the
// tick box, so a person can actually read what they are agreeing to.

const MOMENTS = {
  request: ["terms", "privacy", "shipping"],
  purchase: ["purchase-agreement"],
};

export function requiredAt(moment) {
  return (MOMENTS[moment] || [])
    .map((key) => LEGAL_DOCS.find((doc) => doc.key === key))
    .filter(Boolean);
}
